import type { DraftSegment, PointTuple } from "./types";

// Seam-allowance cut lines, ported from v1's @tailor-frame/seam-allowance
// (generateCutLine and its join resolution). The v1 types and geometry helpers
// it relies on are reproduced below; the adapter at the bottom speaks v2 types.

interface Point2D {
  x: number;
  y: number;
}

export interface LineEdge {
  type: "line";
  id: string;
  start: Point2D;
  end: Point2D;
}

interface CubicBezierEdge {
  type: "cubic-bezier";
  id: string;
  start: Point2D;
  control1: Point2D;
  control2: Point2D;
  end: Point2D;
}

type PatternEdge = LineEdge | CubicBezierEdge;

export interface EdgeLoop {
  edges: PatternEdge[];
  closed: true;
  seamAllowance?: SeamAllowanceNode[];
}

export interface SeamAllowanceRule {
  edgeId: string;
  width: number;
  join: "miter" | "round" | "bevel";
}

export interface SeamAllowanceNode {
  id: string;
  edgeBeforeId: string;
  edgeAfterId: string;
  position: Point2D;
  before: number;
  after: number;
  join: "miter" | "round" | "bevel";
}

const point = (x: number, y: number): Point2D => ({ x, y });
const add = (a: Point2D, b: Point2D): Point2D => point(a.x + b.x, a.y + b.y);
const subtract = (a: Point2D, b: Point2D): Point2D => point(a.x - b.x, a.y - b.y);
const scale = (a: Point2D, amount: number): Point2D => point(a.x * amount, a.y * amount);
const distance = (a: Point2D, b: Point2D): number => Math.hypot(b.x - a.x, b.y - a.y);
const dot = (a: Point2D, b: Point2D): number => a.x * b.x + a.y * b.y;
const leftNormal = (v: Point2D): Point2D => point(-v.y, v.x);

function normalize(v: Point2D): Point2D {
  const magnitude = Math.hypot(v.x, v.y);
  return magnitude === 0 ? point(0, 0) : point(v.x / magnitude, v.y / magnitude);
}

function cubicBezierPoint(start: Point2D, c1: Point2D, c2: Point2D, end: Point2D, t: number): Point2D {
  const mt = 1 - t;
  return point(
    mt * mt * mt * start.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * end.x,
    mt * mt * mt * start.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * end.y,
  );
}

function polygonSignedArea(points: Point2D[]): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

function intersectLines(a1: Point2D, a2: Point2D, b1: Point2D, b2: Point2D): Point2D | undefined {
  const dax = a2.x - a1.x;
  const day = a2.y - a1.y;
  const dbx = b2.x - b1.x;
  const dby = b2.y - b1.y;
  const denominator = dax * dby - day * dbx;
  if (Math.abs(denominator) < 1e-9) return undefined;
  const t = ((b1.x - a1.x) * dby - (b1.y - a1.y) * dbx) / denominator;
  return point(a1.x + t * dax, a1.y + t * day);
}

export function generateCutLoop(
  stitchLine: EdgeLoop,
  allowances: SeamAllowanceNode[] | SeamAllowanceRule[] = stitchLine.seamAllowance ?? [],
  curveSteps = 48,
): EdgeLoop {
  const flattened = flattenEdges(stitchLine.edges, curveSteps);
  const nodes = resolveNodeAllowances(stitchLine, allowances);
  const edgeJoins = resolveOffsetJoins(stitchLine, nodes);
  const clockwise = loopIsClockwise(stitchLine, curveSteps);
  const edgeWidths = edgeWidthMap(stitchLine, nodes);
  const segments = flattened.map((segment) => {
    const widths = edgeWidths.get(segment.sourceEdgeId) ?? { start: 10, end: 10 };
    const width = interpolate(widths.start, widths.end, (segment.tStart + segment.tEnd) / 2);
    return offsetSegment(segment, width, clockwise);
  });

  const edges = cutLineEdgesFromOffsetSegments(stitchLine, segments, edgeJoins, nodes);

  return {
    closed: true,
    edges,
  };
}

function cutLineEdgesFromOffsetSegments(
  stitchLine: EdgeLoop,
  segments: OffsetSegment[],
  edgeJoins: Map<string, Point2D>,
  nodes: SeamAllowanceNode[],
): LineEdge[] {
  const edges: LineEdge[] = [];
  let cutIndex = 0;
  const nodeByAfterEdge = new Map(nodes.map((node) => [node.edgeAfterId, node]));

  for (let edgeIndex = 0; edgeIndex < stitchLine.edges.length; edgeIndex += 1) {
    const sourceEdge = stitchLine.edges[edgeIndex];
    const previousEdge = stitchLine.edges[(edgeIndex - 1 + stitchLine.edges.length) % stitchLine.edges.length];
    const nextEdge = stitchLine.edges[(edgeIndex + 1) % stitchLine.edges.length];
    const edgeSegments = segments.filter((segment) => segment.sourceEdgeId === sourceEdge.id);
    const previousSegments = segments.filter((segment) => segment.sourceEdgeId === previousEdge.id);
    if (edgeSegments.length === 0) {
      continue;
    }

    const startNode = nodeByAfterEdge.get(sourceEdge.id);
    const endNode = nodeByAfterEdge.get(nextEdge.id);
    const rawStart = edgeSegments[0].offsetStart;
    const rawEnd = edgeSegments[edgeSegments.length - 1].offsetEnd;
    const previousRawEnd = previousSegments[previousSegments.length - 1]?.offsetEnd;
    const internalGeneratedStart = isInternalGeneratedBoundary(previousEdge.id, sourceEdge.id);
    const internalGeneratedEnd = isInternalGeneratedBoundary(sourceEdge.id, nextEdge.id);
    const mixedZeroStart = !internalGeneratedStart
      && startNode
      && isMixedZeroWidthNode(startNode)
      && distance(previousEdge.end, sourceEdge.start) < 1e-6;
    const mixedZeroEnd = !internalGeneratedEnd
      && endNode
      && isMixedZeroWidthNode(endNode)
      && distance(sourceEdge.end, nextEdge.start) < 1e-6;
    const hasMeaningfulRoundStart = !mixedZeroStart
      && !internalGeneratedStart
      && startNode?.join === "round"
      && previousRawEnd
      && distance(previousRawEnd, rawStart) >= 0.5;
    if (hasMeaningfulRoundStart) {
      cutIndex = appendCutPolyline(edges, sourceEdge.id, roundJoinPoints(startNode.position, previousRawEnd, rawStart), cutIndex);
    }
    if (mixedZeroStart && previousRawEnd && distance(previousRawEnd, rawStart) >= 0.5) {
      cutIndex = appendCutPolyline(edges, sourceEdge.id, [previousRawEnd, rawStart], cutIndex);
    }

    const start = internalGeneratedStart
      ? rawStart
      : mixedZeroStart
        ? rawStart
        : startNode?.join === "round"
      ? hasMeaningfulRoundStart ? rawStart : previousRawEnd ?? rawStart
      : edgeJoins.get(joinKey(previousEdge.id, sourceEdge.id)) ?? rawStart;
    const end = internalGeneratedEnd
      ? rawEnd
      : mixedZeroEnd
        ? rawEnd
      : endNode?.join === "round"
        ? rawEnd
        : edgeJoins.get(joinKey(sourceEdge.id, nextEdge.id)) ?? rawEnd;
    const startT = segmentParameterNearPoint(edgeSegments, start);
    const endT = segmentParameterNearPoint(edgeSegments, end);
    const points = cutPointsForEdgeSegments(edgeSegments, start, end, startT, endT);

    cutIndex = appendCutPolyline(edges, sourceEdge.id, points, cutIndex);
  }

  return edges;
}

function appendCutPolyline(edges: LineEdge[], sourceEdgeId: string, points: Point2D[], startIndex: number): number {
  let cutIndex = startIndex;

  for (let pointIndex = 0; pointIndex < points.length - 1; pointIndex += 1) {
    if (distance(points[pointIndex], points[pointIndex + 1]) < 1e-6) {
      continue;
    }

    edges.push({
      type: "line",
      id: `cut-${sourceEdgeId}-${cutIndex}`,
      start: points[pointIndex],
      end: points[pointIndex + 1],
    });
    cutIndex += 1;
  }

  return cutIndex;
}

function roundJoinPoints(center: Point2D, start: Point2D, end: Point2D): Point2D[] {
  const startRadius = distance(center, start);
  const endRadius = distance(center, end);
  const radius = (startRadius + endRadius) / 2;
  if (radius < 1e-6 || distance(start, end) < 1e-6) {
    return [start, end];
  }

  const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
  const endAngle = Math.atan2(end.y - center.y, end.x - center.x);
  let delta = endAngle - startAngle;
  while (delta > Math.PI) {
    delta -= Math.PI * 2;
  }
  while (delta < -Math.PI) {
    delta += Math.PI * 2;
  }

  const steps = Math.max(2, Math.min(8, Math.ceil(Math.abs(delta) / (Math.PI / 12))));
  return Array.from({ length: steps + 1 }, (_, index) => {
    if (index === 0) {
      return start;
    }
    if (index === steps) {
      return end;
    }
    const angle = startAngle + delta * (index / steps);
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  });
}

function segmentParameterNearPoint(segments: OffsetSegment[], point: Point2D): number {
  let bestT = segments[0]?.tStart ?? 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const segment of segments) {
    const localT = Math.max(0, Math.min(1, localSegmentT(segment.offsetStart, segment.offsetEnd, point)));
    const projection = add(segment.offsetStart, scale(subtract(segment.offsetEnd, segment.offsetStart), localT));
    const projectionDistance = distance(point, projection);
    if (projectionDistance < bestDistance) {
      bestDistance = projectionDistance;
      bestT = interpolate(segment.tStart, segment.tEnd, localT);
    }
  }

  return bestT;
}

function cutPointsForEdgeSegments(
  segments: OffsetSegment[],
  start: Point2D,
  end: Point2D,
  startT: number,
  endT: number,
): Point2D[] {
  if (endT <= startT + 1e-6) {
    return [start, end];
  }

  const boundedEndT = Math.min(endT, 1);
  const points = [
    start,
    ...segments
      .filter((segment) => segment.tEnd > startT + 1e-6 && segment.tEnd < boundedEndT - 1e-6)
      .map((segment) => segment.offsetEnd),
    end,
  ];

  return points.filter((point, index) => index === 0 || distance(points[index - 1], point) > 1e-6);
}

interface FlattenedSegment {
  sourceEdgeId: string;
  start: Point2D;
  end: Point2D;
  tStart: number;
  tEnd: number;
}

function flattenEdges(edges: PatternEdge[], curveSteps: number): FlattenedSegment[] {
  return edges.flatMap((edge) => {
    if (edge.type === "line") {
      return [{ sourceEdgeId: edge.id, start: edge.start, end: edge.end, tStart: 0, tEnd: 1 }];
    }

    const points = Array.from({ length: curveSteps + 1 }, (_, index) => {
      const t = index / curveSteps;
      return cubicBezierPoint(edge.start, edge.control1, edge.control2, edge.end, t);
    });

    return points.slice(0, -1).map((start, index) => ({
      sourceEdgeId: edge.id,
      start,
      end: points[index + 1],
      tStart: index / curveSteps,
      tEnd: (index + 1) / curveSteps,
    }));
  });
}

function flattenedLoopPoints(edges: PatternEdge[], curveSteps: number): Point2D[] {
  const segments = flattenEdges(edges, curveSteps);
  if (segments.length === 0) {
    return [];
  }

  return [
    segments[0].start,
    ...segments.map((segment) => segment.end),
  ];
}

function loopIsClockwise(loop: EdgeLoop, curveSteps = 64): boolean {
  return polygonSignedArea(flattenedLoopPoints(loop.edges, curveSteps)) > 0;
}

interface OffsetSegment extends FlattenedSegment {
  offsetStart: Point2D;
  offsetEnd: Point2D;
}

interface OffsetJoinSegment {
  sourceEdgeId: string;
  offsetStart: Point2D;
  offsetEnd: Point2D;
}

interface PatternEdgeOffset {
  geometry: PatternEdge;
  seamAllowance: { start: number; end: number };
  offsetGeometry: PatternEdge;
}

interface JoinCandidate {
  point: Point2D;
  previousT: number;
  currentT: number;
  bounded: boolean;
  distanceToSource: number;
  extensionDistance: number;
}

function resolveNodeAllowances(
  stitchLine: EdgeLoop,
  allowances: SeamAllowanceNode[] | SeamAllowanceRule[],
): SeamAllowanceNode[] {
  if (isNodeAllowances(allowances)) {
    return allowances;
  }

  const ruleMap = new Map(allowances.map((rule) => [rule.edgeId, rule]));
  return stitchLine.edges.map((edge, index) => {
    const previous = stitchLine.edges[(index - 1 + stitchLine.edges.length) % stitchLine.edges.length];
    const beforeRule = ruleMap.get(previous.id);
    const afterRule = ruleMap.get(edge.id);
    return {
      id: `node-${previous.id}-${edge.id}`,
      edgeBeforeId: previous.id,
      edgeAfterId: edge.id,
      position: edge.start,
      before: beforeRule?.width ?? 10,
      after: afterRule?.width ?? 10,
      join: joinForRules(previous, beforeRule, afterRule),
    };
  });
}

function joinForRules(
  previousEdge: PatternEdge,
  beforeRule: SeamAllowanceRule | undefined,
  afterRule: SeamAllowanceRule | undefined,
): SeamAllowanceNode["join"] {
  if (beforeRule?.join === "round" && previousEdge.type === "cubic-bezier") {
    return "round";
  }

  return afterRule?.join ?? beforeRule?.join ?? "miter";
}

function isNodeAllowances(allowances: SeamAllowanceNode[] | SeamAllowanceRule[]): allowances is SeamAllowanceNode[] {
  return allowances.length > 0 && "edgeBeforeId" in allowances[0];
}

function edgeWidthMap(stitchLine: EdgeLoop, nodes: SeamAllowanceNode[]): Map<string, { start: number; end: number }> {
  const nodeByAfterEdge = new Map(nodes.map((node) => [node.edgeAfterId, node]));
  const nodeByBeforeEdge = new Map(nodes.map((node) => [node.edgeBeforeId, node]));

  return new Map(stitchLine.edges.map((edge) => [
    edge.id,
    {
      start: nodeByAfterEdge.get(edge.id)?.after ?? 10,
      end: nodeByBeforeEdge.get(edge.id)?.before ?? 10,
    },
  ]));
}

function resolveOffsetJoins(stitchLine: EdgeLoop, nodes: SeamAllowanceNode[]): Map<string, Point2D> {
  const edgeWidths = edgeWidthMap(stitchLine, nodes);
  const offsets = stitchLine.edges.map<PatternEdgeOffset>((edge) => {
    const widths = edgeWidths.get(edge.id) ?? { start: 10, end: 10 };
    return {
      geometry: edge,
      seamAllowance: widths,
      offsetGeometry: offsetEdge(edge, widths.start, widths.end, stitchLine),
    };
  });

  return new Map(nodes.map((node) => {
    const previous = offsets.find((edge) => edge.geometry.id === node.edgeBeforeId);
    const current = offsets.find((edge) => edge.geometry.id === node.edgeAfterId);
    if (!previous || !current) {
      return [joinKey(node.edgeBeforeId, node.edgeAfterId), node.position] as const;
    }

    return [
      joinKey(node.edgeBeforeId, node.edgeAfterId),
      resolveOffsetJoin(previous, current, node.position, stitchLine),
    ] as const;
  }));
}

function resolveOffsetJoin(
  previous: PatternEdgeOffset,
  current: PatternEdgeOffset,
  sourceVertex: Point2D,
  stitchLine: EdgeLoop,
): Point2D {
  const candidates = [
    ...boundedJoinCandidates(previous.offsetGeometry, current.offsetGeometry, sourceVertex),
    ...extendedJoinCandidates(previous.offsetGeometry, current.offsetGeometry, sourceVertex),
  ].filter((candidate) => isValidJoinCandidate(candidate, previous.offsetGeometry, current.offsetGeometry));

  const selected = candidates.sort(compareJoinCandidates)[0];
  if (selected) {
    return selected.point;
  }

  const beforeSegment = offsetJoinSegment(previous.geometry, previous.seamAllowance.end, stitchLine, "end");
  const afterSegment = offsetJoinSegment(current.geometry, current.seamAllowance.start, stitchLine, "start");
  return joinedPoint(beforeSegment, afterSegment);
}

function boundedJoinCandidates(previous: PatternEdge, current: PatternEdge, sourceVertex: Point2D): JoinCandidate[] {
  const previousPolyline = edgePolyline(previous, 64);
  const currentPolyline = edgePolyline(current, 64);
  const candidates: JoinCandidate[] = [];

  for (let previousIndex = 0; previousIndex < previousPolyline.length - 1; previousIndex += 1) {
    for (let currentIndex = 0; currentIndex < currentPolyline.length - 1; currentIndex += 1) {
      const intersection = intersectBoundedSegments(
        previousPolyline[previousIndex],
        previousPolyline[previousIndex + 1],
        currentPolyline[currentIndex],
        currentPolyline[currentIndex + 1],
      );
      if (!intersection) {
        continue;
      }

      const previousSegmentT = localSegmentT(previousPolyline[previousIndex], previousPolyline[previousIndex + 1], intersection);
      const currentSegmentT = localSegmentT(currentPolyline[currentIndex], currentPolyline[currentIndex + 1], intersection);
      candidates.push({
        point: intersection,
        previousT: (previousIndex + previousSegmentT) / (previousPolyline.length - 1),
        currentT: (currentIndex + currentSegmentT) / (currentPolyline.length - 1),
        bounded: true,
        distanceToSource: distance(intersection, sourceVertex),
        extensionDistance: 0,
      });
    }
  }

  return dedupeCandidates(candidates);
}

function extendedJoinCandidates(previous: PatternEdge, current: PatternEdge, sourceVertex: Point2D): JoinCandidate[] {
  const previousEnd = previous.end;
  const currentStart = current.start;
  const previousDirection = normalize(edgeEndTangent(previous));
  const currentDirection = normalize(edgeStartTangent(current));
  const intersection = intersectLines(
    previousEnd,
    add(previousEnd, previousDirection),
    currentStart,
    add(currentStart, currentDirection),
  );

  if (!intersection) {
    return [];
  }

  const previousTangentDistance = dot(subtract(intersection, previousEnd), previousDirection);
  const currentTangentDistance = dot(subtract(intersection, currentStart), currentDirection);
  return [{
    point: intersection,
    previousT: 1 + previousTangentDistance / Math.max(edgeLengthEstimate(previous), 1),
    currentT: currentTangentDistance / Math.max(edgeLengthEstimate(current), 1),
    bounded: false,
    distanceToSource: distance(intersection, sourceVertex),
    extensionDistance: Math.max(Math.max(previousTangentDistance, 0), Math.max(-currentTangentDistance, 0)),
  }];
}

function isValidJoinCandidate(candidate: JoinCandidate, previous: PatternEdge, current: PatternEdge): boolean {
  const previousExtensionLimit = Math.max(30, edgeLengthEstimate(previous) * 0.75);
  const currentExtensionLimit = Math.max(30, edgeLengthEstimate(current) * 0.75);
  const previousCanReach = candidate.previousT >= -1e-6
    && (candidate.previousT <= 1 + previousExtensionLimit / Math.max(edgeLengthEstimate(previous), 1));
  const currentCanReach = candidate.currentT <= 1 + 1e-6
    && (candidate.currentT >= -currentExtensionLimit / Math.max(edgeLengthEstimate(current), 1));

  return previousCanReach && currentCanReach && Number.isFinite(candidate.point.x) && Number.isFinite(candidate.point.y);
}

function compareJoinCandidates(a: JoinCandidate, b: JoinCandidate): number {
  if (a.bounded !== b.bounded) {
    return a.bounded ? -1 : 1;
  }

  return a.distanceToSource - b.distanceToSource
    || a.extensionDistance - b.extensionDistance
    || Math.abs(a.previousT - 1) + Math.abs(a.currentT)
      - (Math.abs(b.previousT - 1) + Math.abs(b.currentT));
}

function offsetEdge(edge: PatternEdge, startWidth: number, endWidth: number, loop: EdgeLoop): PatternEdge {
  if (edge.type === "line") {
    return {
      ...edge,
      start: add(edge.start, offsetForVector(subtract(edge.end, edge.start), startWidth, loop)),
      end: add(edge.end, offsetForVector(subtract(edge.end, edge.start), endWidth, loop)),
    };
  }

  const startOffset = offsetForVector(edgeStartTangent(edge), startWidth, loop);
  const endOffset = offsetForVector(edgeEndTangent(edge), endWidth, loop);
  return {
    ...edge,
    start: add(edge.start, startOffset),
    control1: add(edge.control1, startOffset),
    control2: add(edge.control2, endOffset),
    end: add(edge.end, endOffset),
  };
}

function edgePolyline(edge: PatternEdge, steps: number): Point2D[] {
  if (edge.type === "line") {
    return [edge.start, edge.end];
  }

  return Array.from({ length: steps + 1 }, (_, index) => (
    cubicBezierPoint(edge.start, edge.control1, edge.control2, edge.end, index / steps)
  ));
}

function intersectBoundedSegments(a1: Point2D, a2: Point2D, b1: Point2D, b2: Point2D): Point2D | undefined {
  const intersection = intersectLines(a1, a2, b1, b2);
  if (!intersection) {
    return undefined;
  }

  return pointWithinSegment(intersection, a1, a2) && pointWithinSegment(intersection, b1, b2)
    ? intersection
    : undefined;
}

function pointWithinSegment(point: Point2D, start: Point2D, end: Point2D): boolean {
  const epsilon = 1e-6;
  return point.x >= Math.min(start.x, end.x) - epsilon
    && point.x <= Math.max(start.x, end.x) + epsilon
    && point.y >= Math.min(start.y, end.y) - epsilon
    && point.y <= Math.max(start.y, end.y) + epsilon
    && distanceFromSegment(point, start, end) <= 1e-4;
}

function localSegmentT(start: Point2D, end: Point2D, point: Point2D): number {
  const vector = subtract(end, start);
  const lengthSquared = dot(vector, vector);
  return lengthSquared === 0 ? 0 : dot(subtract(point, start), vector) / lengthSquared;
}

function dedupeCandidates(candidates: JoinCandidate[]): JoinCandidate[] {
  return candidates.filter((candidate, index) => (
    candidates.findIndex((other) => distance(candidate.point, other.point) < 1e-4) === index
  ));
}

function edgeLengthEstimate(edge: PatternEdge): number {
  const points = edgePolyline(edge, edge.type === "line" ? 1 : 24);
  return points.slice(1).reduce((total, point, index) => total + distance(points[index], point), 0);
}

function distanceFromSegment(point: Point2D, start: Point2D, end: Point2D): number {
  const t = Math.max(0, Math.min(1, localSegmentT(start, end, point)));
  const projection = add(start, scale(subtract(end, start), t));
  return distance(point, projection);
}

function offsetJoinSegment(edge: PatternEdge, width: number, loop: EdgeLoop, endpoint: "start" | "end"): OffsetJoinSegment {
  const tangent = endpoint === "start" ? edgeStartTangent(edge) : edgeEndTangent(edge);
  const offset = offsetForVector(tangent, width, loop);
  const anchor = endpoint === "start" ? edge.start : edge.end;
  const direction = normalize(tangent);
  return {
    sourceEdgeId: edge.id,
    offsetStart: add(anchor, offset),
    offsetEnd: add(add(anchor, offset), direction),
  };
}

function edgeStartTangent(edge: PatternEdge): Point2D {
  if (edge.type === "line") {
    return subtract(edge.end, edge.start);
  }

  return firstUsableVector(
    subtract(edge.control1, edge.start),
    subtract(edge.control2, edge.start),
    subtract(edge.end, edge.start),
  );
}

function edgeEndTangent(edge: PatternEdge): Point2D {
  if (edge.type === "line") {
    return subtract(edge.end, edge.start);
  }

  return firstUsableVector(
    subtract(edge.end, edge.control2),
    subtract(edge.end, edge.control1),
    subtract(edge.end, edge.start),
  );
}

function offsetForVector(vector: Point2D, width: number, loop: EdgeLoop): Point2D {
  const clockwise = loopIsClockwise(loop);
  const normal = clockwise ? scale(leftNormal(vector), -1) : leftNormal(vector);
  return scale(normalize(normal), width);
}

function firstUsableVector(...vectors: Point2D[]): Point2D {
  return vectors.find((vector) => vectorLength(vector) > 1e-9) ?? { x: 0, y: 0 };
}

function vectorLength(vector: Point2D): number {
  return Math.hypot(vector.x, vector.y);
}

function generatedSourceEdgeId(edgeId: string): string {
  let current = edgeId;
  while (true) {
    const match = /^(?:cut|net)-(.+)-\d+$/.exec(current);
    if (!match) {
      return current;
    }
    current = match[1];
  }
}

function isInternalGeneratedBoundary(edgeBeforeId: string, edgeAfterId: string): boolean {
  const beforeSource = generatedSourceEdgeId(edgeBeforeId);
  const afterSource = generatedSourceEdgeId(edgeAfterId);
  return beforeSource === afterSource
    && (edgeBeforeId !== beforeSource || edgeAfterId !== afterSource);
}

function isMixedZeroWidthNode(node: SeamAllowanceNode): boolean {
  const beforeZero = Math.abs(node.before) < 1e-6;
  const afterZero = Math.abs(node.after) < 1e-6;
  return beforeZero !== afterZero;
}

function joinKey(edgeBeforeId: string, edgeAfterId: string): string {
  return `${edgeBeforeId}->${edgeAfterId}`;
}

function offsetSegment(segment: FlattenedSegment, width: number, clockwise: boolean): OffsetSegment {
  const vector = subtract(segment.end, segment.start);
  const normal = clockwise ? scale(leftNormal(vector), -1) : leftNormal(vector);
  const offset = scale(normalize(normal), width);
  return {
    ...segment,
    offsetStart: add(segment.start, offset),
    offsetEnd: add(segment.end, offset),
  };
}

function joinedPoint(previous: OffsetSegment | OffsetJoinSegment, current: OffsetSegment | OffsetJoinSegment): Point2D {
  const intersection = intersectLines(previous.offsetStart, previous.offsetEnd, current.offsetStart, current.offsetEnd);
  if (intersection && isReasonableJoin(intersection, previous, current)) {
    return intersection;
  }

  return midpoint(previous.offsetEnd, current.offsetStart);
}

function midpoint(a: Point2D, b: Point2D): Point2D {
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  };
}

function isReasonableJoin(point: Point2D, previous: OffsetSegment | OffsetJoinSegment, current: OffsetSegment | OffsetJoinSegment): boolean {
  const previousLength = Math.max(distance(previous.offsetStart, previous.offsetEnd), 1);
  const currentLength = Math.max(distance(current.offsetStart, current.offsetEnd), 1);
  const localGap = distance(previous.offsetEnd, current.offsetStart);
  const maxDistance = Math.max(20, localGap * 4, Math.min(previousLength, currentLength) * 6);

  return distance(point, previous.offsetEnd) <= maxDistance
    && distance(point, current.offsetStart) <= maxDistance;
}

function interpolate(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

/** One edge of a piece outline, in loop order and oriented to follow the loop. */
export interface CutLineEdge {
  id: string;
  segments: DraftSegment[];
  /** Seam allowance in millimetres; 0 for folds and edges with allowance included. */
  width: number;
}

/**
 * The cut line around a closed piece outline, split by the outline edge each
 * stretch belongs to. Corners are mitred, and an edge with allowance runs on
 * until it meets the neighbouring seam line, so the pieces join without gaps.
 */
export function cutLineForLoop(edges: CutLineEdge[], curveSteps = 48): { sourceEdgeId: string; points: PointTuple[] }[] {
  const toPoint = ([x, y]: PointTuple): Point2D => point(x, y);
  const loopEdges: PatternEdge[] = [];
  const rules: SeamAllowanceRule[] = [];
  const owner = new Map<string, string>();
  for (const edge of edges) {
    edge.segments.forEach((segment, index) => {
      const id = index === 0 ? edge.id : `${edge.id}#${index}`;
      owner.set(id, edge.id);
      rules.push({ edgeId: id, width: edge.width, join: "miter" });
      loopEdges.push(segment.kind === "line"
        ? { type: "line", id, start: toPoint(segment.start), end: toPoint(segment.end) }
        : { type: "cubic-bezier", id, start: toPoint(segment.start), control1: toPoint(segment.control1), control2: toPoint(segment.control2), end: toPoint(segment.end) });
    });
  }
  const cut = generateCutLoop({ closed: true, edges: loopEdges }, rules, curveSteps);
  const runs: { sourceEdgeId: string; points: PointTuple[] }[] = [];
  for (const edge of cut.edges) {
    const generatedFrom = edge.id.replace(/^cut-/, "").replace(/-\d+$/, "");
    const sourceEdgeId = owner.get(generatedFrom) ?? generatedFrom;
    const last = runs[runs.length - 1];
    if (last?.sourceEdgeId === sourceEdgeId) last.points.push([edge.end.x, edge.end.y]);
    else runs.push({ sourceEdgeId, points: [[edge.start.x, edge.start.y], [edge.end.x, edge.end.y]] });
  }
  return runs;
}
